import { User } from "../../../core/models/entities/User";
import { CreateUserDto } from "../../../core/models/dtos/CreateUserDto";
import { Dialog_VM, DialogFrame, useDialog } from "../Dialogs/dialog-base";
import { Section } from "../StructureElements/Section";
import { SectionHeader } from "../../screens/my-profile/SectionHeader";
import { PrimaryButton } from "../Buttons/PrimaryButton";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faPlus, faTrash } from "@fortawesome/free-solid-svg-icons";
import { SectionContent } from "../../screens/my-profile/SectionContent";
import React, { useEffect, useState } from "react";
import { InputWrapper } from "../Input/InputWrapper";
import { InputLabel } from "../Input/InputLabel";
import { CustomInput } from "../Input/CustomInput";
import { CustomPhoneInput } from "../Input/CustomPhoneInput";
import { nameOf } from "../../../utils/functions/ReflectionUtil";
import { SectionHeaderTypography } from "../Typographies/SectionHeaderTypography";
import { BoxHeaderTypography } from "../Typographies/BoxHeaderTypography";
import { CustomInputChangeEvent } from "../../../utils/models/events/CustomInputChangeEvent";
import { formatPhoneNumberIntl } from "react-phone-number-input";

export function UserList({
  users,
  onCreate,
  onDelete,
  onToggle,
}: {
  users: User[];
  onCreate?: (user: CreateUserDto) => Promise<any>;
  onDelete?: (userId: number) => Promise<void>;
  onToggle?: (isOpen: boolean) => void;
}) {
  const [userState, setUserState] = useState(users);
  const [isOpen, setIsOpen] = useState(false);

  useEffect(() => {
    setUserState(users);
  }, [users]);

  const createUserDialog = useDialog();

  function handleCreateUser(user: CreateUserDto) {
    if (onCreate) onCreate(user).then(createUserDialog.closeDialog);
  }

  async function handleDeleteUser(user: User) {
    if (!onDelete) return;
    if (confirm(`Are you sure you want to delete the user ${user.email}`)) {
      await onDelete(user.id);
      setUserState(userState.filter((u) => u.id != user.id));
    }
  }

  return (
    <Section>
      <CreateUserDialog
        dialogOptions={createUserDialog}
        onCreate={handleCreateUser}
      />
      <SectionHeader
        isCloseable={true}
        initialState={isOpen}
        onToggle={(open) => {
          setIsOpen(open);
          onToggle?.(open);
        }}
      >
        <SectionHeaderTypography>Users</SectionHeaderTypography>
      </SectionHeader>
      <SectionContent close={!isOpen}>
        <div className={"border rounded flex-1 overflow-y-scroll"}>
          <table className={"table-auto w-full"}>
            <thead className={"text-left sticky top-0 bg-[#FCFDFE]"}>
              <tr>
                <th className={"py-3 pl-4 font-medium text-gray-600 text-xs"}>
                  First Name
                </th>
                <th className={"py-3 pr-4 font-medium text-gray-600 text-xs"}>
                  Last Name
                </th>
                <th className={"py-3 px-4 font-medium text-gray-600 text-xs"}>
                  Email
                </th>
                <th className={"py-3 px-4 font-medium text-gray-600 text-xs"}>
                  Phone
                </th>
                <th
                  className={"py-3 px-4 font-medium text-gray-600 text-xs w-20"}
                >
                  Actions
                </th>
              </tr>
            </thead>
            <tbody>
              {userState.map((x) => (
                <tr key={x.id} className={"border-b hover:bg-gray-50"}>
                  <td className={"py-2 pl-4 text-xs"}>{x.firstName}</td>
                  <td className={"py-2 pr-4 text-xs"}>{x.lastName}</td>
                  <td className={"py-2 px-4 text-xs"}>{x.email}</td>
                  <td className={"py-2 px-4 text-xs"}>
                    {x.phone ? formatPhoneNumberIntl(x.phone) : ""}
                  </td>
                  <td className={"py-2 px-4 text-xs"}>
                    <PrimaryButton
                      width={"sm"}
                      className={"px-4"}
                      onClick={() => handleDeleteUser(x)}
                    >
                      <FontAwesomeIcon icon={faTrash} />
                      <span className="flex-1">Delete</span>
                    </PrimaryButton>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
          <div className={"m-4"}>
            <PrimaryButton
              width={"md"}
              className={"px-4"}
              onClick={createUserDialog.openDialog}
            >
              <FontAwesomeIcon icon={faPlus} />
              <span className="flex-1">Create new User</span>
            </PrimaryButton>
          </div>
        </div>
      </SectionContent>
    </Section>
  );
}

interface CreateUserDialogProps {
  dialogOptions: Dialog_VM;
  onCreate: (user: CreateUserDto) => void;
}

function CreateUserDialog({ dialogOptions, onCreate }: CreateUserDialogProps) {
  const [createUser, setCreateUser] = useState<CreateUserDto>({
    email: "",
    firstname: "",
    lastname: "",
    phone: "",
    organizationId: 0,
  });

  function handleInputChange(
    event: React.ChangeEvent<HTMLInputElement> | CustomInputChangeEvent
  ) {
    const target = event.target;
    const value = target.type === "checkbox" ? target.checked : target.value;
    const name = target.name;

    setCreateUser((x) => ({
      ...x,
      [name]: value,
    }));
  }

  function handleSubmit() {
    onCreate(createUser);
  }

  return (
    <DialogFrame dialogOption={dialogOptions}>
      <div className={"space-y-6"}>
        <BoxHeaderTypography>Create a new user</BoxHeaderTypography>
        <div className={"space-y-4"}>
          <InputWrapper>
            <InputLabel>First name</InputLabel>
            <CustomInput
              name={nameOf<CreateUserDto>((e) => e.firstname)}
              value={createUser.firstname}
              onChange={(e) => handleInputChange(e)}
            />
          </InputWrapper>
          <InputWrapper>
            <InputLabel>Last name</InputLabel>
            <CustomInput
              name={nameOf<CreateUserDto>((e) => e.lastname)}
              value={createUser.lastname}
              onChange={(e) => handleInputChange(e)}
            />
          </InputWrapper>
          <InputWrapper>
            <InputLabel>Email</InputLabel>
            <CustomInput
              name={nameOf<CreateUserDto>((e) => e.email)}
              value={createUser.email}
              onChange={(e) => handleInputChange(e)}
            />
          </InputWrapper>
          <InputWrapper>
            <InputLabel>Phone</InputLabel>
            <CustomPhoneInput
              name={nameOf<CreateUserDto>((e) => e.phone)}
              value={createUser.phone}
              onChange={(e) => handleInputChange(e)}
              disabled={false}
            />
          </InputWrapper>
          <div className={"flex justify-center"}>
            <PrimaryButton width={"md"} onClick={handleSubmit}>
              Create
            </PrimaryButton>
          </div>
        </div>
      </div>
    </DialogFrame>
  );
}
